import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import RichTextEditor from '@/components/admin/RichTextEditor';
import ImageUpload from '@/components/admin/ImageUpload';
import AiBlogAssistant from '@/components/admin/AiBlogAssistant';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: 'Vocavi Insights Team',
    category: 'Industry Trends',
    image_url: '',
    status: 'published'
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/admin/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      if (editingBlog) {
        await axios.put(`${API}/admin/blogs/${editingBlog.id}`, formData, config);
        toast.success('Blog updated successfully');
      } else {
        await axios.post(`${API}/admin/blogs`, formData, config);
        toast.success('Blog created successfully');
      }
      setIsModalOpen(false);
      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error('Failed to save blog');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog deleted');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData(blog);
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingBlog(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      author: 'Vocavi Insights Team',
      category: 'Industry Trends',
      image_url: '',
      status: 'published'
    });
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <div data-testid="admin-blogs-page">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-primary">Manage Blogs</h1>
        <Button onClick={() => { resetForm(); setIsModalOpen(true); }} data-testid="add-blog-button">
          <Plus className="mr-2" size={20} /> Add Blog
        </Button>
      </div>

      <div className="bg-white shadow-md">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Author</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {blogs.map((blog) => (
                <tr key={blog.id} data-testid={`blog-row-${blog.id}`}>
                  <td className="px-6 py-4 text-sm font-medium text-primary">{blog.title}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{blog.category}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{blog.author}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 text-xs font-semibold ${blog.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {blog.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <button onClick={() => handleEdit(blog)} data-testid={`edit-blog-${blog.id}`} className="text-blue-600 hover:text-blue-800">
                      <Edit size={18} />
                    </button>
                    <button onClick={() => handleDelete(blog.id)} data-testid={`delete-blog-${blog.id}`} className="text-red-600 hover:text-red-800">
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="blog-modal">
          <DialogHeader>
            <DialogTitle>{editingBlog ? 'Edit Blog' : 'Add New Blog'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                data-testid="blog-title-input"
                onChange={(e) => {
                  const title = e.target.value;
                  setFormData({ ...formData, title, slug: generateSlug(title) });
                }}
                className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Slug *</label>
              <input
                type="text"
                required
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Category *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                >
                  <option value="Industry Trends">Industry Trends</option>
                  <option value="Leadership">Leadership</option>
                  <option value="Career Advice">Career Advice</option>
                  <option value="Company News">Company News</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status *</label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Author *</label>
              <input
                type="text"
                required
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Cover Image</label>
              <ImageUpload
                value={formData.image_url}
                onChange={(image_url) => setFormData({ ...formData, image_url })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Excerpt *</label>
              <textarea
                required
                rows={3}
                data-testid="blog-excerpt-input"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
              />
            </div>

            <AiBlogAssistant
              title={formData.title}
              content={formData.content}
              category={formData.category}
              onApplyContent={(content) => setFormData((f) => ({ ...f, content }))}
              onApplyExcerpt={(excerpt) => setFormData((f) => ({ ...f, excerpt }))}
              onApplyTitle={(title) => setFormData((f) => ({ ...f, title, slug: generateSlug(title) }))}
            />

            <div>
              <label className="block text-sm font-medium mb-1">Content *</label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => setFormData({ ...formData, content })}
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-blog-button">
                {editingBlog ? 'Update Blog' : 'Create Blog'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setIsModalOpen(false); resetForm(); }}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlogs;